import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAgents } from "@/contexts/AgentContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  CheckCircle,
  XCircle,
  Clock,
  Building2,
  Loader2,
  AlertCircle,
  UserPlus,
} from "lucide-react";

interface TeamInvitation {
  id: string;
  organization_id: string;
  email: string;
  status: string;
  expires_at: string;
  invited_by: string;
  organization?: {
    id: string;
    name: string;
    logo_url?: string;
  };
  inviter?: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    full_name?: string;
  };
}

function TeamInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshProfile, refreshOrganization } = useAuth();
  const { refreshAgents } = useAgents();
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [invitationStatus, setInvitationStatus] = useState<
    "valid" | "expired" | "invalid" | "already-member" | "already-accepted"
  >("valid");
  const [invitation, setInvitation] = useState<TeamInvitation | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNameForm, setShowNameForm] = useState(false);

  useEffect(() => {
    const loadInvitation = async () => {
      if (!token) {
        setInvitationStatus("invalid");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Fetch invitation details
        const { data: invitationData, error: invitationError } = await supabase
          .from("team_invitations")
          .select(
            `
            *,
            organization:organizations(*),
            inviter:profiles!team_invitations_invited_by_fkey(
              id,
              email,
              first_name,
              last_name,
              full_name
            )
          `,
          )
          .eq("token", token)
          .single();

        if (invitationError || !invitationData) {
          console.error("Error fetching invitation:", invitationError);
          setInvitationStatus("invalid");
          setIsLoading(false);
          return;
        }

        setInvitation(invitationData);

        // Check if invitation is already accepted
        if (invitationData.status === "accepted") {
          setInvitationStatus("already-accepted");
          setIsLoading(false);
          return;
        }

        // Check if invitation is expired
        if (
          invitationData.status === "expired" ||
          new Date() > new Date(invitationData.expires_at)
        ) {
          setInvitationStatus("expired");
          // Update status in database if not already marked as expired
          if (invitationData.status !== "expired") {
            await supabase
              .from("team_invitations")
              .update({ status: "expired" })
              .eq("id", invitationData.id);
          }
          setIsLoading(false);
          return;
        }

        // SECURITY CHECK: Verify that the logged-in user matches the invitation email
        if (
          user &&
          user.email?.toLowerCase() !== invitationData.email?.toLowerCase()
        ) {
          console.warn(
            `Email mismatch: invitation for ${invitationData.email}, but user is ${user.email}. Logging out...`,
          );

          // Store invitation details for after logout
          const redirectUrl = `/accept-invite?token=${token}${searchParams.get("org") ? `&org=${searchParams.get("org")}` : ""}`;
          sessionStorage.setItem("authRedirect", redirectUrl);
          sessionStorage.setItem("invitationEmail", invitationData.email);

          // Log out the current user and redirect back to accept-invite page
          await supabase.auth.signOut();
          navigate(
            `/accept-invite?token=${token}${searchParams.get("org") ? `&org=${searchParams.get("org")}` : ""}`,
          );
          return;
        }

        // Check if user is already a member
        if (user) {
          const { data: existingProfile } = await supabase
            .from("profiles")
            .select("id")
            .eq("id", user.id)
            .eq("organization_id", invitationData.organization_id)
            .single();

          if (existingProfile) {
            setInvitationStatus("already-member");
            setIsLoading(false);
            return;
          }
        }

        setInvitationStatus("valid");
      } catch (error) {
        console.error("Error loading invitation:", error);
        setInvitationStatus("invalid");
      } finally {
        setIsLoading(false);
      }
    };

    loadInvitation();
  }, [token, user]);

  const handleProceedToAccept = () => {
    if (!user) {
      // Store current URL for redirect after login
      const orgId = searchParams.get("org") || invitation?.organization_id;
      navigate(
        `/login?redirect=/invite/${token}${orgId ? `?org=${orgId}` : ""}`,
      );
      return;
    }

    // Check if user already has name set in their profile
    const userHasName =
      user?.user_metadata?.first_name && user?.user_metadata?.last_name;

    if (userHasName) {
      // User already has name, proceed directly
      handleAcceptInvitation();
    } else {
      // Show name form
      setShowNameForm(true);
    }
  };

  const handleAcceptInvitation = async () => {
    // Validate name if showing form
    if (showNameForm && (!firstName.trim() || !lastName.trim())) {
      toast({
        variant: "destructive",
        title: "Name required",
        description: "Please enter your first and last name to continue.",
      });
      return;
    }

    // Validate password if showing form
    if (showNameForm) {
      if (!password || password.length < 8) {
        toast({
          variant: "destructive",
          title: "Password required",
          description: "Password must be at least 8 characters long.",
        });
        return;
      }

      if (password !== confirmPassword) {
        toast({
          variant: "destructive",
          title: "Passwords don't match",
          description: "Please ensure both passwords match.",
        });
        return;
      }
    }

    if (!invitation) {
      toast({
        variant: "destructive",
        title: "Invalid invitation",
        description: "Unable to process this invitation.",
      });
      return;
    }

    console.log(
      `[TeamInvitationPage] Starting invitation acceptance process for invitation:`,
      invitation?.id,
    );
    console.log(`[TeamInvitationPage] User ID:`, user?.id);
    console.log(`[TeamInvitationPage] User email:`, user?.email);

    setIsAccepting(true);
    try {
      // STEP 1: Set password if user is setting up their account
      if (showNameForm && password) {
        console.log("[TeamInvitationPage] Setting user password...");
        const { error: passwordError } = await supabase.auth.updateUser({
          password: password,
        });

        if (passwordError) {
          console.error(
            "[TeamInvitationPage] Error setting password:",
            passwordError,
          );
          throw new Error("Failed to set password: " + passwordError.message);
        }

        console.log(
          "[TeamInvitationPage] ✅ Password set successfully for user",
        );
      }

      // STEP 2: Prepare profile update data
      const profileUpdate: {
        organization_id: string;
        joined_at: string;
        first_name?: string;
        last_name?: string;
        full_name?: string;
      } = {
        organization_id: invitation.organization_id,
        joined_at: new Date().toISOString(),
      };

      // Add name if provided through form
      if (showNameForm && firstName && lastName) {
        profileUpdate.first_name = firstName.trim();
        profileUpdate.last_name = lastName.trim();
        profileUpdate.full_name = `${firstName.trim()} ${lastName.trim()}`;
      }

      // STEP 3: Check if profile exists, create it if not
      console.log(
        `[TeamInvitationPage] Checking if profile exists for user ${user.id}`,
      );
      const { data: existingProfile, error: profileCheckError } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", user.id)
        .single();

      if (profileCheckError && profileCheckError.code !== "PGRST116") {
        console.error(
          `[TeamInvitationPage] Error checking profile:`,
          profileCheckError,
        );
        throw profileCheckError;
      }

      if (!existingProfile) {
        // Profile doesn't exist, create it
        console.log(
          `[TeamInvitationPage] Profile does not exist, creating new profile for user ${user.id}`,
        );
        console.log(`[TeamInvitationPage] Profile data:`, {
          id: user.id,
          email: user.email,
          ...profileUpdate,
        });

        const { error: createError } = await supabase.from("profiles").insert({
          id: user.id,
          email: user.email,
          first_name:
            (showNameForm
              ? firstName.trim()
              : user.user_metadata?.first_name) || null,
          last_name:
            (showNameForm ? lastName.trim() : user.user_metadata?.last_name) ||
            null,
          full_name:
            showNameForm && firstName && lastName
              ? `${firstName.trim()} ${lastName.trim()}`
              : [user.user_metadata?.first_name, user.user_metadata?.last_name]
                  .filter(Boolean)
                  .join(" ") || null,
          ...profileUpdate,
        });

        if (createError) {
          console.error(
            `[TeamInvitationPage] Error creating profile:`,
            createError,
          );
          throw createError;
        }

        console.log(
          `[TeamInvitationPage] Profile created successfully for user ${user.id}`,
        );
      } else {
        // Profile exists, update it
        console.log(
          `[TeamInvitationPage] Profile exists, updating for user ${user.id}`,
        );
        console.log(`[TeamInvitationPage] Update data:`, profileUpdate);

        const { error: updateError } = await supabase
          .from("profiles")
          .update(profileUpdate)
          .eq("id", user.id);

        if (updateError) {
          console.error(
            `[TeamInvitationPage] Error updating profile:`,
            updateError,
          );
          throw updateError;
        }

        console.log(
          `[TeamInvitationPage] Profile updated successfully for user ${user.id}`,
        );
      }

      // STEP 4: Mark invitation as accepted
      console.log(
        `[TeamInvitationPage] Marking invitation ${invitation.id} as accepted`,
      );
      const { error: invitationError } = await supabase
        .from("team_invitations")
        .update({
          status: "accepted",
          accepted_at: new Date().toISOString(),
          accepted_by: user.id,
          profile_id: user.id, // Link to the profile that was created/updated
        })
        .eq("id", invitation.id);

      if (invitationError) {
        console.error(
          `[TeamInvitationPage] Error updating invitation:`,
          invitationError,
        );
        throw invitationError;
      }

      console.log(
        `[TeamInvitationPage] Invitation marked as accepted successfully`,
      );

      // STEP 5: Refresh all contexts to load the new organization data
      console.log(
        "[TeamInvitationPage] Refreshing all contexts after accepting invitation",
      );

      // Refresh profile first to get the updated organization_id
      refreshProfile();

      // Then refresh organization data to load the new organization's details
      await refreshOrganization();

      // Refresh agents to load the organization's agents
      await refreshAgents();

      toast({
        title: "Welcome to the team! 🎉",
        description: `You've successfully joined ${invitation.organization?.name || "the organization"}. Loading your new workspace...`,
      });

      // Navigate to home where they'll see their new organization's data
      // The contexts are now refreshed, so no need for page reload
      console.log(
        `[TeamInvitationPage] Invitation acceptance completed successfully, navigating to home`,
      );
      setTimeout(() => {
        navigate("/");
      }, 1000);
    } catch (error) {
      console.error("Error accepting invitation:", error);
      toast({
        variant: "destructive",
        title: "Failed to accept invitation",
        description: "Please try again or contact the person who invited you.",
      });
    } finally {
      setIsAccepting(false);
    }
  };

  const handleDeclineInvitation = () => {
    navigate("/");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Loading invitation...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (invitationStatus === "invalid") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <XCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>
              This invitation link is invalid or has been removed
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button
              className="w-full"
              variant="outline"
              onClick={() => navigate("/")}
            >
              Go to Homepage
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (invitationStatus === "expired") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
              <Clock className="h-6 w-6 text-amber-600" />
            </div>
            <CardTitle>Invitation Expired</CardTitle>
            <CardDescription>
              This invitation has expired. Please ask{" "}
              {invitation?.inviter?.full_name ||
                invitation?.inviter?.email ||
                "the inviter"}{" "}
              to send you a new one.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex-col gap-3">
            <Button
              className="w-full"
              variant="outline"
              onClick={() => navigate("/")}
            >
              Go to Homepage
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              Contact: {invitation?.inviter?.email}
            </p>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (invitationStatus === "already-member") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-blue-600" />
            </div>
            <CardTitle>Already a Member</CardTitle>
            <CardDescription>
              You're already a member of{" "}
              {invitation?.organization?.name || "this organization"}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button className="w-full" onClick={() => navigate("/")}>
              Go to Dashboard
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Handle already accepted invitations
  if (invitationStatus === "already-accepted") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle>Invitation Already Accepted</CardTitle>
            <CardDescription>
              This invitation has already been used to join{" "}
              {invitation?.organization?.name || "the organization"}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button className="w-full" onClick={() => navigate("/")}>
              Go to Dashboard
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="relative">
              <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
                {invitation?.organization?.logo_url ? (
                  <img
                    src={invitation.organization.logo_url}
                    alt={invitation.organization.name}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                ) : (
                  <Building2 className="h-10 w-10 text-primary" />
                )}
              </div>
              <div className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full bg-green-500 flex items-center justify-center">
                <UserPlus className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>

          <div>
            <CardTitle className="text-2xl">
              Join {invitation?.organization?.name || "the team"}
            </CardTitle>
            <CardDescription className="mt-2">
              {invitation?.inviter?.full_name ||
                invitation?.inviter?.email ||
                "A team member"}{" "}
              has invited you to join their team
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Organization Info */}
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Organization
              </span>
              <span className="font-medium">
                {invitation?.organization?.name || "Unknown"}
              </span>
            </div>
          </div>

          {/* What you'll get */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              By accepting this invitation, you'll be able to collaborate on AI
              agents, access shared resources, and work together with your team.
            </AlertDescription>
          </Alert>

          {/* Expiry Notice */}
          <div className="text-center text-sm text-muted-foreground">
            This invitation expires on{" "}
            {invitation
              ? new Date(invitation.expires_at).toLocaleDateString()
              : ""}
          </div>
        </CardContent>

        <CardFooter>
          {!showNameForm ? (
            <div className="flex gap-3 w-full">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleDeclineInvitation}
                disabled={isAccepting}
              >
                Decline
              </Button>
              <Button
                className="flex-1"
                onClick={handleProceedToAccept}
                disabled={isAccepting}
              >
                {user ? "Accept Invitation" : "Sign in to Accept"}
              </Button>
            </div>
          ) : (
            <div className="w-full space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    placeholder="Enter your first name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={isAccepting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    placeholder="Enter your last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={isAccepting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Set Your Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Minimum 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isAccepting}
                  />
                  <p className="text-xs text-muted-foreground">
                    Create a password to secure your account
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isAccepting}
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowNameForm(false)}
                  disabled={isAccepting}
                >
                  Back
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleAcceptInvitation}
                  disabled={isAccepting}
                >
                  {isAccepting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Joining...
                    </>
                  ) : (
                    "Complete & Join"
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}

export default TeamInvitationPage;
